import {
	FaceDetectionEventSchema,
	FlaggedKeywordEventSchema,
	ObjectDetectionEventSchema,
	ZodToReadable,
} from "@yujacorp/verity-api-contract";
import apiClient from "../../../../../helpers/api-client";
import { VideoEvent } from "../../../../utilities/useTimestamps";
import { RecordingSourceVideoUrls } from "../ProctoringVideoPlayer";
import { SessionInfo } from "../../../TestResultsWrapper";

type LoggingEvents = Exclude<
	Awaited<ReturnType<typeof apiClient.proctoringSession.getSessionLoggedEvents>>,
	null | undefined
>;

/**
 * getLoggedEvents will make an HTTP GET request to the node-server for the session with uuid: sessionId
 */
export async function getLoggedEvents(sessionId: string, courseId: number): Promise<LoggingEvents> {
	try {
		return await apiClient.proctoringSession.getSessionLoggedEvents({ sessionId, courseId });
	} catch (e) {
		return { events: [], anomalies: [], integrityScore: 100 };
	}
}

export function findStartDate(loggingEvents: LoggingEvents): Date {
	const startProctoringEvent = loggingEvents.events.find((e) => {
		return e && e.type === "session" && e.name === "start proctoring" && e.time && e.details?.success;
	});
	return new Date(startProctoringEvent?.time ?? loggingEvents.events?.[0].time);
}

export function convertLoggingEventsToVideoEvents({
	startDate,
	loggingEvents,
}: {
	startDate?: Date;
	loggingEvents?: LoggingEvents;
}): VideoEvent[] | null {
	if (!loggingEvents?.events.length || loggingEvents.events.length === 0) {
		return null;
	}
	if (!startDate) {
		startDate = findStartDate(loggingEvents);
	}
	const videoEvents: VideoEvent[] = [];
	let forceStopProctoringEvent: VideoEvent | undefined;
	for (const loggingEvent of loggingEvents.events) {
		let anomaly: { comment?: string | null; flagLevel: number; resolved: boolean } | undefined;
		let flag = 0;
		if (loggingEvent.anomalyRef != null) {
			anomaly = loggingEvents.anomalies.find((x) => x.id === loggingEvent.anomalyRef);
			const changeFlag = anomaly?.flagLevel ?? -1;
			if (changeFlag !== -1) {
				flag = changeFlag;
			}
		}
		const time = (new Date(loggingEvent.time).getTime() - startDate.getTime()) / 1000;
		const videoEvent: VideoEvent = {
			key: `event-${loggingEvent.id}`,
			id: loggingEvent.id,
			time: time,
			name: loggingEvent.name,
			type: loggingEvent.type,
			details: loggingEvent.details,
			flag_level: flag,
			logged_time: loggingEvent.time,
			comment: anomaly?.comment ?? undefined,
			resolved: anomaly?.resolved ?? false,
		};
		videoEvents.push(videoEvent);
		if (loggingEvent.name === "force stop proctoring") {
			forceStopProctoringEvent = videoEvent;
		}
		if (loggingEvent.name === "force stop proctoring message" && forceStopProctoringEvent) {
			forceStopProctoringEvent.details.message = loggingEvent.details.message;
		}
	}
	const endProctoringEventTime = videoEvents.findLast((e) => e.type === "session" && e.name === "end proctoring")?.time;

	// add anomalies that are not referenced by events
	const anomalyRefs = loggingEvents.events
		.map((event) => event.anomalyRef)
		.filter((ref): ref is number => typeof ref === "number");
	const videoAnomalies: VideoEvent[] = [];
	for (const anomaly of loggingEvents.anomalies) {
		if (!anomalyRefs.includes(anomaly.id)) {
			// prevent duplicate anomalies events at same time
			if (
				anomaly.timeStart &&
				!videoAnomalies.find((x) => x.type === anomaly.type && x.logged_time === anomaly.timeStart)
			) {
				videoAnomalies.push({
					key: `anomaly-${anomaly.id}`,
					time: (new Date(anomaly.timeStart).getTime() - startDate.getTime()) / 1000,
					name: anomaly.type,
					type: anomaly.type,
					details: { start: true },
					flag_level: anomaly.flagLevel,
					resolved: anomaly.resolved,
					logged_time: anomaly.timeStart,
				});
			}
			if (
				anomaly.timeEnd &&
				anomaly.timeEnd !== anomaly.timeStart &&
				!videoAnomalies.find((x) => x.type === anomaly.type && x.logged_time === anomaly.timeEnd)
			) {
				videoAnomalies.push({
					key: `anomaly-${anomaly.id}`,
					time: (new Date(anomaly.timeEnd).getTime() - startDate.getTime()) / 1000,
					name: anomaly.type,
					type: anomaly.type,
					details: { start: false },
					flag_level: 0,
					resolved: false,
					logged_time: anomaly.timeEnd,
				});
			}
		}
	}
	videoEvents.push(...videoAnomalies);
	if (endProctoringEventTime) {
		return videoEvents.filter(
			(x) =>
				x.time <= endProctoringEventTime ||
				(x.type === "session" && (x.name === "no submission" || x.name === "upload incomplete")),
		);
	}
	return videoEvents;
}

export async function getSessionNotes(sessionId: string, courseId: number) {
	try {
		return await apiClient.proctoringSession.getSessionNotes({ sessionId, courseId });
	} catch (e) {
		console.error(e);
	}
	throw new Error("Failed to get session note");
}

export async function getRoomScanNotes(sessionId: string, courseId: number) {
	try {
		return await apiClient.proctoringSession.getRoomScanNotes({ sessionId, courseId });
	} catch (e) {
		console.error(e);
	}
	throw new Error("Failed to get session note");
}

export async function getVideoUrls(sessionId: string, recordingType: string): Promise<RecordingSourceVideoUrls> {
	try {
		return apiClient.proctoringSession.getVideoUrls({ sessionId, recordingType });
	} catch (e) {
		return {};
	}
}

export async function getRoomScanVideoUrl(sessionId: string): Promise<{ [key: string]: string } | null> {
	try {
		const url = `/api/verity/quiz/recording/${sessionId}/room_scan`;
		const response = await fetch(url, {
			mode: "cors",
			credentials: "same-origin",
		});
		if (response.status === 200) {
			const res = (await response.json()) as { [url: string]: string };
			return {
				"0": res.url,
			};
		}
		throw new Error("Unable to get room scan video");
	} catch (e) {
		return null;
	}
}

export function areSourcesAvailable(sources: RecordingSourceVideoUrls): boolean {
	return Object.values(sources).some((source) => Object.keys(source).length > 0);
}

export function filterVideoEvents(events: VideoEvent[]): VideoEvent[] {
	// If there exists a gap between events of more than 4 hours, trim the events to the one right before the gap
	const FOUR_HOURS_IN_SECONDS = 4 * 60 * 60;
	let trimIndex = events.length;

	// Find the first gap of more than 4 hours
	for (let i = 0; i < events.length - 1; i++) {
		const currentEvent = events[i];
		const nextEvent = events[i + 1];
		const gap = nextEvent.time - currentEvent.time;
		if (gap > FOUR_HOURS_IN_SECONDS) {
			trimIndex = i + 1;
			break;
		}
	}

	// Trim events to just before the gap
	const trimmedEvents = events.slice(0, trimIndex);

	// Find the latest time among recording end and quiz end events
	const latestEndTime = trimmedEvents.reduce((latestTime, event) => {
		if (event.type === "session") {
			if (
				event.name === "end proctoring" ||
				event.name === "end quiz" ||
				event.name === "end recording" ||
				event.name === "force stop proctoring" ||
				event.name === "no submission"
			) {
				return Math.max(latestTime, event.time);
			}
		}
		return latestTime;
	}, 0);

	// If we have neither, return the trimmed events
	if (latestEndTime === 0) {
		return trimmedEvents;
	}

	// Filter out events that occur after the latest end time
	return trimmedEvents.filter(
		(event) => event.time <= latestEndTime || (event.type === "session" && event.name === "upload incomplete"),
	);
}

export function addDurationMetaDataEvents(events: VideoEvent[]): VideoEvent[] {
	const attemptQuestionEvents = events.filter((e) => e.type === "assessment" && e.name === "attempt question");
	const endQuizEvent = events.find((e) => e.type === "session" && e.name === "end quiz");
	const lastEvent = events.length > 0 ? events[events.length - 1] : undefined;
	const attemptDurationsMap = new Map<VideoEvent, number>();

	for (let i = 0; i < attemptQuestionEvents.length; i++) {
		const currentEvent = attemptQuestionEvents[i];
		const nextEvent = attemptQuestionEvents[i + 1];

		let durationSeconds: number;
		if (nextEvent) {
			durationSeconds = nextEvent.time - currentEvent.time;
		} else if (endQuizEvent) {
			durationSeconds = endQuizEvent.time - currentEvent.time;
		} else if (lastEvent) {
			durationSeconds = lastEvent.time - currentEvent.time;
		} else {
			continue; // Skip storing if we can't calculate a duration
		}

		attemptDurationsMap.set(currentEvent, durationSeconds * 1000);
	}

	return events
		.map((x) => {
			if (x.type === "machine-learning" && x.name !== "flagged-keyword") {
				const machineLearningEvent = x as unknown as
					ZodToReadable<typeof ObjectDetectionEventSchema> | ZodToReadable<typeof FaceDetectionEventSchema>;
				const nextMatchingEvent = events.find((e) => {
					if (e.type !== "machine-learning") return false;
					const event = e as unknown as
						ZodToReadable<typeof ObjectDetectionEventSchema> | ZodToReadable<typeof FaceDetectionEventSchema>;
					return event.name === machineLearningEvent.name && event.time > machineLearningEvent.time;
				}) as unknown as
					| ((ZodToReadable<typeof ObjectDetectionEventSchema> | ZodToReadable<typeof FaceDetectionEventSchema>) & {
							ids?: number[];
					  })
					| undefined;
				if (!nextMatchingEvent) return x;

				const duration = Number(nextMatchingEvent.time) - Number(machineLearningEvent.time);
				return {
					...x,
					details: {
						...x.details,
						metadata: { duration: duration * 1000 },
					},
				};
			} else if (x.type === "assessment" && x.name === "attempt question") {
				const durationMs = attemptDurationsMap.get(x);
				if (durationMs === undefined) return x;
				return {
					...x,
					details: {
						...x.details,
						metadata: { duration: durationMs },
					},
				};
			} else {
				return x;
			}
		})
		.filter((e): e is VideoEvent => e !== null)
		.filter((e) => {
			if (e.type !== "machine-learning") return true;
			const event = e as unknown as
				| ZodToReadable<typeof ObjectDetectionEventSchema>
				| ZodToReadable<typeof FaceDetectionEventSchema>
				| ZodToReadable<typeof FlaggedKeywordEventSchema>;
			return (
				(event.name === "object-detection" && event.details.currentState !== "detected-no-object") ||
				(event.name === "face-detection" && event.details.currentState !== "detected-target-face") ||
				event.name === "flagged-keyword"
			);
		});
}

export function getStartingSessionIndex(proctoringSessions: SessionInfo[], initialProctoringSessionId?: string) {
	const initialProctoringSessionIndex = initialProctoringSessionId
		? proctoringSessions.findIndex(({ uuid }) => uuid === initialProctoringSessionId)
		: -1;
	if (initialProctoringSessionIndex !== -1) {
		return initialProctoringSessionIndex;
	}
	const statusPriority = ["flagged", "unflagged", "incomplete", "unavailable", "pending", "error"];
	for (const status of statusPriority) {
		const statusSessionIndex = proctoringSessions.findIndex((session) => session.status === status);
		if (statusSessionIndex !== -1) {
			return statusSessionIndex;
		}
	}
	return Math.max(0, proctoringSessions.length - 1); // default to latest instead of first
}

export function waitForVideoLoad(videoEl: HTMLVideoElement) {
	return new Promise((resolve) => {
		videoEl.addEventListener("load", function loadListener() {
			videoEl.removeEventListener("load", loadListener);
			resolve(true);
		});
		setTimeout(() => resolve(false), 3000);
	});
}

export function loadVideoAtTime(videoEl: HTMLVideoElement, time: number) {
	return new Promise((resolve, reject) => {
		videoEl.addEventListener("canplay", function canPlayListener() {
			videoEl.removeEventListener("canplay", canPlayListener);
			resolve(true);
		});
		videoEl.addEventListener("error", function errorListener() {
			videoEl.removeEventListener("error", errorListener);
			reject();
		});
		setTimeout(() => resolve(false), 3000);

		videoEl.currentTime = time;
	});
}

/**
 * Goes through the api-client rather than handing `<img>` the path directly: an image load is not
 * `window.fetch`, so on the Lumina surface it escapes the rewrite in `lumina/results-api.ts` and reaches
 * the stock unscoped `/photo` route instead of the token-scoped mirror.
 */
export async function getIdPhotoSrc(sessionUuid: string, type: "id" | "person") {
	try {
		return await apiClient.proctoringSession.getPhotoUrl(sessionUuid, type);
	} catch (e) {
		console.error(e);
		return undefined;
	}
}

export async function getSignatureSrc(sessionUuid: string) {
	try {
		const url = await apiClient.proctoringSession.getPhotoUrl(sessionUuid, "signature");
		return url;
	} catch (e) {
		console.error(e);
		throw new Error("Failed to get session signature");
	}
}

export async function getScratchNoteLinks(sessionUuid: string) {
	try {
		return await apiClient.scratchNote.getLinks({ proctoringSessionUuid: sessionUuid });
	} catch (e) {
		console.error(e);
		throw new Error("Failed to get scratch note links");
	}
}

/**
 * Reference material always ends up in the student's own submission bucket — either uploaded there directly
 * (student mode), or snapshotted there from the instructor's bucket on the student's first view of the
 * toolbar (instructor mode, see add-toolbar.tsx). So the result page only ever needs to read from there.
 * This is viewed by the instructor on the results page, so it goes through the instructor-permissioned endpoint.
 */
export async function getReferenceMaterials(sessionUuid: string) {
	try {
		return await apiClient.proctoringSession.getSubmissionReferenceMaterials(sessionUuid);
	} catch (e) {
		console.error(e);
		throw new Error("Failed to get reference materials");
	}
}

/**
 * Permanently removes one attempt and all of its proctoring data. Unlike `deleteRecording` below, which
 * only tombstones an attempt's media and leaves the attempt itself in the selector.
 */
export async function deleteAttempt(sessionUuid: string) {
	await apiClient.proctoringSession.deleteProctoringSession(sessionUuid);
}

export async function deleteRecording(sessionUuid: string, type: "recording" | "roomScan") {
	if (type === "recording") {
		await apiClient.proctoringSession.deleteRecordingsFromSession(sessionUuid);
	} else {
		await apiClient.proctoringSession.deleteRoomScanFromSession(sessionUuid);
	}
}
